import type { SessionStatus } from '@visious/core';
import { useSession } from '../../platform/session';
import { text } from '../../strings';

/**
 * §8's signed-out behaviour and §13.2, as a page: Subscriptions, your playlists and History are
 * **disabled with a sign-in affordance** rather than hidden, and never shown broken or backed by a
 * local store.
 *
 * The gate is its own screen rather than a branch inside each of the three, for the reason §8 gives
 * for keeping `/post/ID` out of the 404: a page that can render an empty list will render one for a
 * missing credential, and "no subscriptions" is the wrong sentence about "not signed in". It arrives
 * now, one slice before the three feeds it gates, because the nav has linked to them since 2a — and
 * until 3b–3d land, *what* it gates is the honest "this is next" placeholder.
 *
 * R13: the decision is here, `view.tsx` renders it.
 */

export type GatedFeed = 'subscriptions' | 'playlists' | 'history' | 'channels';

interface GateCopy {
  readonly title: string;
  readonly detail: string;
  /** What still works with no account at all — so the page is not only about what is missing. */
  readonly anonymous: string;
}

/** R7's other side again: core owns the gate (`NavDestination.requiresAuth`), the words are ours. */
function gateCopy(): Readonly<Record<GatedFeed, GateCopy>> {
  const words = text().gated;
  return {
    subscriptions: words.subscriptions,
    playlists: words.playlists,
    history: words.history,
    channels: words.channels,
  };
}

/**
 * A short second line about the credential itself. Three of the five statuses have one: two where the
 * page is blocked and the token is the reason, and **`under-scoped`, which is not blocked at all** — the
 * gate lets it through, so without this line the page behind it would assert that the token is fine
 * while the endpoint it needs answers `Invalid scope`.
 */
function reasonFor(status: SessionStatus): string | null {
  const words = text().gated.reason;
  switch (status) {
    case 'expired':
      return words.expired;
    case 'unreadable':
      return words.unreadable;
    case 'under-scoped':
      return words.underScoped;
    case 'signed-out':
    case 'active':
      return null;
  }
}

export interface GateBinding {
  /**
   * `unknown` while the platform is still resolving the instance — the token is keyed per instance
   * origin, so there is genuinely no answer yet. Rendering the gate during that moment would flash
   * "sign in" at somebody who is signed in, on every reload.
   */
  readonly state: 'unknown' | 'blocked' | 'available';
  readonly title: string;
  readonly detail: string;
  readonly anonymous: string;
  /** Present in the `available` state too — see {@link reasonFor}. */
  readonly reason: string | null;
  readonly signInHref: string;
}

export interface GateInput {
  readonly feed: GatedFeed;
  /** `/login?next=…`, built by `screenForRoute` from the route it is gating. */
  readonly signInHref: string;
}

export function useGate(input: GateInput): GateBinding {
  const session = useSession();
  const copy = gateCopy()[input.feed];

  return {
    state: !session.ready ? 'unknown' : session.facts.signedIn ? 'available' : 'blocked',
    title: copy.title,
    detail: copy.detail,
    anonymous: copy.anonymous,
    reason: session.ready ? reasonFor(session.facts.status) : null,
    signInHref: input.signInHref,
  };
}
